import { RouteComponentProps } from "react-router-dom";
import {
  Radio,
  Row,
  Image as AntImage,
  Col,
  Form,
  Input,
  Table,
  TableColumnsType,
  TableProps,
  Tag,
  Select,
} from "antd";
import { useTranslation } from "react-i18next";
import SeloHejImg from "shared/assets/images/selo-hej.png";
import { ReactComponent as SeloHejLogo } from "shared/assets/images/logo-selo-hej-letreiro.svg";
import SeloAnnoDominiLogo from "shared/assets/images/logo-anno-domini-letreiro.png";
import SeloKamillaFialhoImg from "shared/assets/images/selo-kamilha-fialho.png";
import SeloAnnoDominiImg from "shared/assets/images/selo-anno-domini.jpg";
import VortexLogo from "shared/assets/images/vortex-logo.png";
import { ReactComponent as FilterIcon } from "shared/assets/images/filter-icon.svg";
import { ReactComponent as ExportIcon } from "shared/assets/images/cloud-download.svg";
import {
  YoutubeFilled,
  InstagramOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { faCheck, faMinus } from "@fortawesome/free-solid-svg-icons";
import { useCallback, useEffect, useState } from "react";
import {
  exportSpecialCampaignRequestCSV,
  exportSpecialCampaignRequestExcel,
  getDistroTokenByLoggedUserTeam,
  getSpecialCampaignRequests,
  updateApprovalSpecialCampaignRequest,
} from "shared/service/StrmService";
import { downloadFile, FileType } from "shared/helpers/download-file";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpotify } from "@fortawesome/free-brands-svg-icons";
import { debounce } from "lodash";
import {
  onClickInstagram,
  onClickSpotify,
  onClickYoutube,
} from "shared/helpers/external-url-helper";
import {
  SpecialCampaignApprovalEnum,
  specialCampaignApprovalList,
} from "shared/enums/SpecialCampaignApprovalEnum";
import DynamicStyledSelect from "./DynamicStyledSelect";
import "./LabelManagement.scss";
import { moneyFormat } from "shared/helpers/BRLFormat";
import { SpecialCampaignRequestFieldsSortEnum } from "shared/enums/SpecialCampaignRequestFieldsSortEnum";
import { useSelector } from "react-redux";
import i18n from "translation";

const defaultPagination = { pageSize: 50, pageNumber: 1 };

// Revelator URL constants
const REVELATOR_TAB_URLS = {
  consume: "consumption",
  revenue: "revenue",
} as const;

const buildRevelatorUrl = (
  tabUrl: string,
  language: string,
  token: string
): string => {
  return `${process.env.REACT_APP_REVELATOR_SITE}authorize?token=${token}&redirectUrl=${process.env.REACT_APP_REVELATOR_SITE}${language}/analytics/${tabUrl}/dashboard`;
};

const approvalEnumIconByValue = (val: SpecialCampaignApprovalEnum) => {
  if (val === SpecialCampaignApprovalEnum.Pending) {
    return <FontAwesomeIcon icon={faMinus} />;
  } else if (val === SpecialCampaignApprovalEnum.Approved) {
    return <FontAwesomeIcon icon={faCheck} />;
  } else if (val === SpecialCampaignApprovalEnum.Disapproved) {
    return <CloseOutlined />;
  } else if (val === SpecialCampaignApprovalEnum.ContractSigned) {
    return <FontAwesomeIcon icon={faCheck} />;
  }
};

const getSpecialCampaignImg = (specialCampaign: string) => {
  let response = "";
  switch (specialCampaign) {
    case "kamilla-fialho":
      response = SeloKamillaFialhoImg;
      break;
    case "henrique-juliano":
      response = SeloHejImg;
      break;
    case "anno-domini":
      response = SeloAnnoDominiImg;
      break;
    case "vortex":
      response = VortexLogo;
      break;

    default:
      break;
  }

  return response;
};

const getSpecialCampaignLogo = (specialCampaign: string) => {
  let response: React.ReactNode = <></>;
  switch (specialCampaign) {
    case "henrique-juliano":
      response = <SeloHejLogo />;
      break;
    case "anno-domini":
      response = (
        <img src={SeloAnnoDominiLogo} alt="Selo Anno Domini" height={50} />
      );
      break;
    case "vortex":
      response = <img src={VortexLogo} alt="Selo Vortex" height={50} />;
      break;

    default:
      break;
  }

  return response;
};

const LabelManagement: React.FC<RouteComponentProps> = (props) => {
  const { history } = props;
  const loggedUser = useSelector((state: any) => state.users.loggedUser);
  const countryOptions = useSelector(
    (state: any) => state.countries.countryOptions
  );
  const team = loggedUser?.userTeam[0]?.team;
  const { t } = useTranslation("radar");
  const [filter, setFilter] = useState<any>({
    orderBy: SpecialCampaignRequestFieldsSortEnum.CreatedOn,
    orderDirection: "descend",
    pagination: defaultPagination,
    searchAll: "",
    approval: null,
    countryCode: null,
    state: "",
    city: "",
    specialCampaign: loggedUser?.specialCampaignTeam,
  });
  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [distroToken, setDistroToken] = useState<any>(null);
  const [enableExport, setEnableExport] = useState<boolean>(false);
  const isRowBlocked = (val: SpecialCampaignApprovalEnum) =>
    val === SpecialCampaignApprovalEnum.ContractSigned;

  const getRequests = useCallback(async () => {
    setLoading(true);
    const response: any = await getSpecialCampaignRequests(filter);
    setData(response?.response);
    setLoading(false);
  }, [filter]);

  const onClickArtist = (artist: any) =>
    artist?.radarArtistId &&
    history.push(`/artist-profile/${artist?.radarArtistId}`);

  const onClickTabsDistro = (tabUrl: string) => {
    const language = i18n.language === "pt" ? "pt-br" : i18n.language;
    const url = buildRevelatorUrl(tabUrl, language, distroToken?.token);
    if (url && distroToken?.token) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const exportFilter = {
    specialCampaignName: filter.specialCampaign,
    approval: filter.approval,
    searchAll: filter.searchAll,
    countryCode: filter.countryCode,
    state: filter.state,
    city: filter.city,
  };

  const exportCsv = async () => {
    const data: any = await exportSpecialCampaignRequestCSV(exportFilter);
    downloadFile(data, "selo-escalavel.csv", FileType.Csv);
    setEnableExport(false);
  };

  const exportExcel = async () => {
    const data: any = await exportSpecialCampaignRequestExcel(exportFilter);
    downloadFile(data, "selo-escalavel.xlsx", FileType.Xlsx);
    setEnableExport(false);
  };

  useEffect(() => {
    getRequests();
  }, [getRequests]);

  useEffect(() => {
    const loadDistroToken = async () => {
      try {
        const token = await getDistroTokenByLoggedUserTeam();
        setDistroToken(token);
      } catch (error) {
        console.error("Error loading distro token:", error);
      }
    };

    loadDistroToken();
  }, []);

  const renderIconPlatforms = (row: any) => {
    return (
      <div style={{ display: "flex", gap: "10px" }}>
        <div
          className={`platform-icon ${row.userArtist.spotifyId && "active"}`}
          onClick={() => onClickSpotify(row.userArtist.spotifyId)}
        >
          <FontAwesomeIcon icon={faSpotify} color="#F2F2F2" />
        </div>
        <div
          className={`platform-icon ${row.userArtist.youtubeId && "active"}`}
        >
          <YoutubeFilled
            onClick={() => onClickYoutube(row.userArtist.youtubeId)}
          />
        </div>
        <div
          className={`platform-icon ${row.userArtist.instagramId && "active"}`}
        >
          <InstagramOutlined
            onClick={() => onClickInstagram(row.userArtist.instagramId)}
          />
        </div>
      </div>
    );
  };

  const renderApprovalCollumn = (
    id: string,
    val: SpecialCampaignApprovalEnum
  ) => {
    return (
      <DynamicStyledSelect
        initialValue={val}
        disabled={isRowBlocked(val)}
        onChange={async (newVal: SpecialCampaignApprovalEnum) => {
          await updateApprovalSpecialCampaignRequest(id, {
            approval: newVal,
          });
        }}
        options={specialCampaignApprovalList.map((x) => {
          return {
            ...x,
            label: (
              <Tag className={`tag-status-cell status-cell-${x.value}`}>
                <span className="icon">{approvalEnumIconByValue(x.value)}</span>
                <span className="text">{t(x.label)}</span>
              </Tag>
            ),
          };
        })}
      />
    );
  };

  const columns: TableColumnsType<any> = [
    {
      title: t("pages.LabelManagement.name"),
      dataIndex: ["userArtist", "name"],
      sorter: true,
      key: SpecialCampaignRequestFieldsSortEnum.Name,
      className: "no-wrap",
      render: (val, record: any) => (
        <>
          <AntImage
            src={record.userArtist.spotifyAvatarUrl}
            className="avatar-user-artist"
          />
          <span onClick={() => onClickArtist(record.userArtist)}>{val}</span>
        </>
      ),
    },
    {
      title: t("pages.LabelManagement.status"),
      dataIndex: "approvalFront",
      sorter: true,
      key: SpecialCampaignRequestFieldsSortEnum.Status,
      render: (val, record) => renderApprovalCollumn(record.id, val),
    },
    {
      title: t("pages.LabelManagement.growthSpeed"),
      dataIndex: ["userArtist", "radarGrowthSpeedIndex"],
      sorter: true,
      key: SpecialCampaignRequestFieldsSortEnum.RadarGrowthSpeedIndex,
      render: (val) => val ?? "-",
    },
    {
      title: t("pages.LabelManagement.projectionOfCatalogRevenue"),
      dataIndex: ["userArtist", "radarGrossFullRevenueForecast1Y"],
      sorter: true,
      key: SpecialCampaignRequestFieldsSortEnum.RadarGrossFullRevenueForecast1Y,
      render: (radarGrossFullRevenueForecast1Y, record) =>
        radarGrossFullRevenueForecast1Y
          ? moneyFormat(
              radarGrossFullRevenueForecast1Y,
              record?.userArtist?.radarCashAdvanceCurrency
            )
          : "-",
    },
    {
      title: t("pages.LabelManagement.level"),
      dataIndex: ["userArtist", "radarStage"],
      sorter: true,
      key: SpecialCampaignRequestFieldsSortEnum.RadarStage,
      render: (val) => val ?? "-",
    },
    {
      title: t("pages.LabelManagement.genre"),
      dataIndex: ["userArtist", "genreCodes"],
      render: (val) => <Tag className="tag-genre">{val}</Tag>,
    },
    {
      title: t("pages.LabelManagement.country"),
      dataIndex: ["userArtist", "countryCode"],
      sorter: true,
      key: SpecialCampaignRequestFieldsSortEnum.CountryCode,
    },
    {
      title: t("pages.LabelManagement.requestedDate"),
      sorter: true,
      key: SpecialCampaignRequestFieldsSortEnum.CreatedOn,
      defaultSortOrder: "descend",
      dataIndex: "createdOn",
      render: (createdOn) => moment(createdOn).format("DD/MM/YYYY"),
    },
    {
      title: t("pages.LabelManagement.plataforms"),
      className: "container-platforms no-wrap",
      render: (_, record) => renderIconPlatforms(record),
    },
  ];

  const rowSelection: TableProps<any>["rowSelection"] = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {},
    getCheckboxProps: (record: any) => ({
      disabled:
        record.approvalFront === SpecialCampaignApprovalEnum.ContractSigned,
      name: record.name,
      className:
        record.approvalFront === SpecialCampaignApprovalEnum.ContractSigned
          ? "checkbox-disabled"
          : undefined,
    }),
  };

  return (
    <div className="label-managment">
      <div className="tab-menu">
        <div className="logo section">
          <AntImage
            src={getSpecialCampaignImg(loggedUser?.specialCampaignTeam)}
            preview={false}
          />
          <div className="right-side">
            <p className="title">{t("pages.LabelManagement.label")}</p>
            <p className="name">{team.displayName}</p>
          </div>
          <div className="menu">
            <Radio.Group
              value={"artist"}
              onChange={async (ev) => {
                const selectedTab = ev.target.value;

                if (selectedTab in REVELATOR_TAB_URLS) {
                  return onClickTabsDistro(selectedTab);
                }
              }}
            >
              <Radio.Button value="artist">
                {t("pages.LabelManagement.artists")}
              </Radio.Button>
              <Radio.Button value="consume" disabled={!distroToken?.token}>
                {t("pages.LabelManagement.consume")}
              </Radio.Button>
              <Radio.Button value="revenue" disabled={!distroToken?.token}>
                {t("pages.LabelManagement.revenue")}
              </Radio.Button>
            </Radio.Group>
          </div>
        </div>
        <div className="section">
          {getSpecialCampaignLogo(loggedUser?.specialCampaignTeam)}
        </div>
      </div>
      <div className="content">
        <Row justify="space-between" align="middle" style={{ gap: 12 }}>
          <Col>
            <Form>
              <Form.Item name="searchAll">
                <Input
                  placeholder={t("pages.LabelManagement.findArtist")}
                  suffix={<SearchOutlined />}
                  className="input-search-all"
                  onChange={debounce(
                    (ev) =>
                      setFilter({
                        ...filter,
                        searchAll: ev.target.value,
                        pagination: {
                          ...filter.pagination,
                          pageNumber: defaultPagination.pageNumber,
                        },
                      }),
                    1000
                  )}
                />
              </Form.Item>
            </Form>
          </Col>
          <Col>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <Select
                options={countryOptions}
                placeholder={t("pages.LabelManagement.country")}
                className="btn-filter"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  (option?.label as string)
                    .toUpperCase()
                    .includes(input.toUpperCase())
                }
                onChange={(value) => {
                  setFilter({
                    ...filter,
                    countryCode: value ?? null,
                    pagination: {
                      ...filter.pagination,
                      pageNumber: defaultPagination.pageNumber,
                    },
                  });
                }}
              />
              <Input
                placeholder={t("pages.LabelManagement.stateFilter")}
                className="input-search-all"
                onChange={debounce(
                  (ev) =>
                    setFilter({
                      ...filter,
                      state: ev.target.value,
                      pagination: {
                        ...filter.pagination,
                        pageNumber: defaultPagination.pageNumber,
                      },
                    }),
                  1000
                )}
              />
              <Input
                placeholder={t("pages.LabelManagement.cityFilter")}
                className="input-search-all"
                onChange={debounce(
                  (ev) =>
                    setFilter({
                      ...filter,
                      city: ev.target.value,
                      pagination: {
                        ...filter.pagination,
                        pageNumber: defaultPagination.pageNumber,
                      },
                    }),
                  1000
                )}
              />
              <Select
                options={specialCampaignApprovalList.map((x) => {
                  return {
                    ...x,
                    label: t(x.label),
                  };
                })}
                placeholder={
                  <span className="placeholder-filter">
                    <FilterIcon className="icon-filter" />{" "}
                    {t("pages.LabelManagement.filter")}
                  </span>
                }
                className="btn-filter"
                showArrow={false}
                showSearch={false}
                allowClear
                onChange={(value) => {
                  setFilter({
                    ...filter,
                    approval: value,
                    pagination: {
                      ...filter.pagination,
                      pageNumber: defaultPagination.pageNumber,
                    },
                  });
                }}
              />
              <div style={{ position: "relative" }}>
                {enableExport && (
                  <div className="container-botoes-exportar">
                    <button
                      className="button-export"
                      onClick={() => exportCsv()}
                    >
                      CSV
                    </button>
                    <button
                      className="button-export"
                      onClick={() => exportExcel()}
                    >
                      Excel
                    </button>
                  </div>
                )}
                <button
                  className="botao-azul button-export"
                  onClick={() => setEnableExport(!enableExport)}
                >
                  {t("pages.LabelManagement.export")}
                  <ExportIcon className="download-icon" />
                </button>
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <Table
              loading={loading}
              rowSelection={{ type: "checkbox", ...rowSelection }}
              columns={columns}
              dataSource={data.items}
              className="table-special-campaign-requests"
              rowClassName={(record: any) => {
                return `status-${record.approvalFront}`;
              }}
              rowKey={"id"}
              pagination={{
                current: data?.pagination?.pageNumber,
                pageSize: data?.pagination?.pageSize,
                total: data?.pagination?.total,
                pageSizeOptions: ["50", "100"],
                showSizeChanger: true,
                className: "selectable-pagination",
              }}
              onChange={(ev, _, sorter) => {
                setFilter({
                  ...filter,
                  orderBy: !Array.isArray(sorter)
                    ? (sorter.columnKey as SpecialCampaignRequestFieldsSortEnum)
                    : SpecialCampaignRequestFieldsSortEnum.None,
                  orderDirection: !Array.isArray(sorter) ? sorter.order : null,
                  pagination: {
                    ...filter.pagination,
                    pageNumber: ev.current ?? defaultPagination.pageNumber,
                    pageSize: ev.pageSize ?? defaultPagination.pageSize,
                  },
                });
              }}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default LabelManagement;
